import React, { ReactElement } from 'react';
import { format, parseISO } from 'date-fns';
import { Trans } from 'react-i18next';
import { ColumnsType } from 'common/components/antd/Table/Table';
import { Tag } from 'common/components/antd/Tag/Tag';
import { renderCopyableCell } from 'common/components/CopyableCell/CopyableCell';
import { renderLoadableCell } from 'common/components/LoadableCell/LoadableCell';
import { StatusText } from 'common/components/StatusText/StatusText';
import TableActionColumnDropdown from 'common/components/TableActionColumnDropdown/TableActionColumnDropdown';
import { TruncatedTextCell } from 'common/components/TruncatedTextCell/TruncatedTextCell';
import { IS_ADMIN_FE } from 'common/constants/general-access-role.constants';
import { alphabeticSortFn, dateSortFn, numericSortFn } from 'common/utils/sort.utils';
import {
  EEmissionHistoryTableColumns,
  EmissionHistoryAssetDirection,
  EmissionHistoryAssetStatus,
} from 'domains/emission/enums/emission.enum';
import { TEmissionHistoryAsset } from 'domains/emission/types/emission.types';

type TColumnsProps = {
  hoveredRowIndex: null | number;
  menuItems: any;
  onClickItemAction: any;
  isDashboard?: boolean;
};

export const getColumns = ({
  hoveredRowIndex,
  menuItems,
  onClickItemAction,
  isDashboard,
}: TColumnsProps): ColumnsType<TEmissionHistoryAsset> => [
  IS_ADMIN_FE
    ? {
        title: <Trans i18nKey="dashboard.emissionHistoryTable.column.counterpartyName" />,
        dataIndex: EEmissionHistoryTableColumns.CounterpartyName,
        width: 211,
        sorter: alphabeticSortFn(EEmissionHistoryTableColumns.CounterpartyName),
        render: renderLoadableCell,
      }
    : {
        title: <Trans i18nKey="dashboard.emissionHistoryTable.column.transactionId" />,
        dataIndex: EEmissionHistoryTableColumns.TransactionId,
        render: (text, record, index) => (
          <TruncatedTextCell hoveredRowIndex={hoveredRowIndex} rowIndex={index}>
            {record.guid}
          </TruncatedTextCell>
        ),
        width: 211,
        sorter: alphabeticSortFn(EEmissionHistoryTableColumns.TransactionId),
        searchable: true,
      },
  {
    title: <Trans i18nKey="dashboard.emissionHistoryTable.column.created" />,
    dataIndex: EEmissionHistoryTableColumns.Created,
    width: 214,
    sorter: dateSortFn(EEmissionHistoryTableColumns.Created),
    render: (text): string => format(parseISO(text), 'dd/LL/yyyy HH:mm:ss'),
  },
  {
    title: <Trans i18nKey="dashboard.emissionHistoryTable.column.initiator" />,
    dataIndex: EEmissionHistoryTableColumns.Initiator,
    width: 213,
    sorter: alphabeticSortFn(EEmissionHistoryTableColumns.Initiator),
  },
  {
    title: <Trans i18nKey="dashboard.emissionHistoryTable.column.approved" />,
    dataIndex: EEmissionHistoryTableColumns.Approved,
    width: 215,
    sorter: alphabeticSortFn(EEmissionHistoryTableColumns.Approved),
  },
  {
    title: <Trans i18nKey="dashboard.emissionHistoryTable.column.amount" />,
    dataIndex: EEmissionHistoryTableColumns.Amount,
    width: 130,
    render: renderCopyableCell,
    sorter: numericSortFn(EEmissionHistoryTableColumns.Amount),
  },
  {
    title: <Trans i18nKey="dashboard.emissionHistoryTable.column.reserveBalance" />,
    dataIndex: EEmissionHistoryTableColumns.ReserveBalance,
    width: 130,
  },
  {
    title: <Trans i18nKey="dashboard.emissionHistoryTable.column.type" />,
    dataIndex: EEmissionHistoryTableColumns.Type,
    width: 196,
    sorter: numericSortFn(EEmissionHistoryTableColumns.Type),
    render: (type: EmissionHistoryAssetDirection): ReactElement => {
      const typeData: Record<EmissionHistoryAssetDirection, { label: string; color: string }> = {
        [EmissionHistoryAssetDirection.Issue]: {
          label: 'common.issue',
          color: 'rgba(160, 217, 17, 1)',
        },
        [EmissionHistoryAssetDirection.Burn]: {
          label: 'common.burn',
          color: 'rgba(250, 140, 22, 1)',
        },
      };

      return (
        <Tag color={typeData[type].color}>
          <Trans i18nKey={typeData[type].label} />
        </Tag>
      );
    },
  },
  {
    title: <Trans i18nKey="dashboard.emissionHistoryTable.column.status" />,
    dataIndex: EEmissionHistoryTableColumns.Status,
    width: 196,
    defaultSortOrder: IS_ADMIN_FE ? 'descend' : null,
    sorter: alphabeticSortFn(EEmissionHistoryTableColumns.Status),
    render: (value: EmissionHistoryAssetStatus): ReactElement => {
      const statusColor: Record<EmissionHistoryAssetStatus, string> = {
        [EmissionHistoryAssetStatus.Created]: '#FFC53D',
        [EmissionHistoryAssetStatus.Confirmed]: '#52C41A',
        [EmissionHistoryAssetStatus.Canceled]: '#FF4D4F',
      };

      const statusLocale: Record<EmissionHistoryAssetStatus, any> = {
        [EmissionHistoryAssetStatus.Created]: <Trans i18nKey="common.created" />,
        [EmissionHistoryAssetStatus.Confirmed]: <Trans i18nKey="common.confirmed" />,
        [EmissionHistoryAssetStatus.Canceled]: <Trans i18nKey="common.canceled" />,
      };

      return <StatusText color={statusColor[value]}>{statusLocale[value]}</StatusText>;
    },
  },
  ...(IS_ADMIN_FE && isDashboard
    ? [
        {
          title: '',
          width: 32,
          key: 'actionBar',
          render: (status: any, record: TEmissionHistoryAsset) => (
            <TableActionColumnDropdown record={record} onClickItemAction={onClickItemAction} menuItems={menuItems} />
          ),
        },
      ]
    : []),
];
